import { api } from "./index";
import {
  InvoiceDetail,
  InvoiceDownloadResponse,
  InvoiceSummary,
  ListInvoicesQuery,
} from "./billingApi";

/**
 * Cross-company invoices for the internal/admin console (VERA-882).
 * Mirrors billingApi's invoice shapes but each record also carries the Vera
 * company it belongs to, and the endpoints are the platform-wide /internal ones.
 */
export interface InternalInvoiceSummary extends InvoiceSummary {
  companyId: string | null;
  companyName: string | null;
}

export interface InternalInvoiceDetail extends InvoiceDetail {
  companyId: string | null;
  companyName: string | null;
}

export interface ListInternalInvoicesResponse {
  invoices: InternalInvoiceSummary[];
  hasMore: boolean;
  nextCursor: string | null;
}

// The internal list adds an optional multi-company filter (server-side): when set,
// only those companies' invoices are returned. Everything else mirrors billingApi's query.
export interface ListInternalInvoicesQuery extends ListInvoicesQuery {
  companyIds?: string[];
}

export const internalInvoicesApi = {
  async listInvoices(
    params?: ListInternalInvoicesQuery
  ): Promise<ListInternalInvoicesResponse> {
    const { companyIds, ...rest } = params ?? {};
    const response = await api.get("/internal/invoices", {
      // Send companyIds as a comma-separated param (the API accepts either that or a
      // repeated param); omit it entirely when no company is selected.
      params: {
        ...rest,
        ...(companyIds && companyIds.length
          ? { companyIds: companyIds.join(",") }
          : {}),
      },
    });
    return response.data;
  },

  async getInvoice(invoiceId: string): Promise<InternalInvoiceDetail> {
    const response = await api.get(`/internal/invoices/${invoiceId}`);
    return response.data;
  },

  async getInvoiceDownloadUrl(
    invoiceId: string
  ): Promise<InvoiceDownloadResponse> {
    const response = await api.get(`/internal/invoices/${invoiceId}/download`);
    return response.data;
  },
};
